import Decimal from 'decimal.js';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Web3Singleton from 'web3Singleton';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';

import { addErrorMessageAction } from 'actions/global-actions';

import { parseFromDecimal } from 'utils/parser.utils';

import { StakingPeriod, StakingPhaseData_V2, StakingTableStatus } from '../types';
import { FINAL_CALL_THRESHOLD, PERIOD_LABELS } from '../constants';
import { determinePhaseStatus, toPeriodDaysEnum } from '../utils';

interface UseProgramDataProps {
  tokenDecimals: number;
  hasCurrency: boolean;
}

interface UseProgramDataReturn {
  currentPhaseIndex: number;
  stakingPhases: StakingPhaseData_V2[];
  minDeposit: string;
  fetchProgramData: () => Promise<void>;
  fetchMinDeposit: () => Promise<void>;
  programError: string | null;
}

/**
 * Hook for fetching and processing program data (phases, APYs, targets).
 * Handles the periodical staking contract program structure.
 */
export const useProgramData = ({
  tokenDecimals,
  hasCurrency,
}: UseProgramDataProps): UseProgramDataReturn => {
  const { t } = useTranslation('notifications');
  const dispatch = useDispatch();
  const lgoWeb3 = Web3Singleton.getInstance();

  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(-1);
  const [stakingPhases, setStakingPhases] = useState<StakingPhaseData_V2[]>([]);
  const [minDeposit, setMinDeposit] = useState('0');
  const [programError, setProgramError] = useState<string | null>(null);

  /**
   * Fetches minimum deposit amount.
   * Uses read-only provider if wallet is not connected.
   */
  const fetchMinDeposit = useCallback(async () => {
    const stakingService = lgoWeb3.getEffectiveStakingService();
    if (!stakingService) return;

    try {
      const minDepositValue = await stakingService.periodicalStakingMinimumDeposit();
      setMinDeposit(minDepositValue);
    } catch (e) {
      console.error('Failed to fetch min deposit:', e);
    }
  }, [lgoWeb3]);

  /**
   * Fetches and processes program data (phases, APYs, targets).
   * Uses read-only provider if wallet is not connected.
   */
  const fetchProgramData = useCallback(async () => {
    if (!hasCurrency) return;

    const stakingService = lgoWeb3.getEffectiveStakingService();
    if (!stakingService) return;

    try {
      const data = await stakingService.getPeriodicalStakingData();
      if (!data) return;

      const currentPhase = parseInt(data[0]);
      const stakingPeriodList: string[] = data[1];
      const phasePeriodTargets: string[][] = data[2];
      const phasePeriodAPYs: string[][] = data[3];
      const phasePeriodStaked: string[][] = data[4];

      setCurrentPhaseIndex(currentPhase);

      // Process phases
      const phases: StakingPhaseData_V2[] = phasePeriodTargets.map((targets, phaseIdx) => {
        const apys = phasePeriodAPYs[phaseIdx];
        const staked = phasePeriodStaked[phaseIdx];
        const status = determinePhaseStatus(phaseIdx, currentPhase);

        // Process periods within phase
        const periods: StakingPeriod[] = stakingPeriodList.map((days, periodIdx) => {
          const target = targets[periodIdx];
          const stakedAmount = staked[periodIdx];
          const remaining = new Decimal(target).sub(stakedAmount).toString();
          const remainingNum = parseFloat(parseFromDecimal(remaining, tokenDecimals));
          const targetNum = parseFloat(parseFromDecimal(target, tokenDecimals));
          const apyValue = parseInt(apys[periodIdx]);
          const isExhausted = remainingNum < 1;
          const isFinalCall = !isExhausted && targetNum > 0 && remainingNum / targetNum <= FINAL_CALL_THRESHOLD;

          return {
            days: toPeriodDaysEnum(days),
            label: PERIOD_LABELS[days] || `${days} Days`,
            apyDisplay: `${apyValue}%`,
            apyValue,
            target,
            staked: stakedAmount,
            remaining,
            remainingDisplay: remainingNum,
            canStake: status === StakingTableStatus.ACTIVE && !isExhausted,
            isFinalCall,
            isExhausted,
          };
        });

        // Calculate phase totals
        const totalTarget = targets.reduce((sum, t) => new Decimal(sum).add(t).toString(), '0');
        const totalStakedPhase = staked.reduce((sum, s) => new Decimal(sum).add(s).toString(), '0');
        const remainingCapacity = new Decimal(totalTarget).sub(totalStakedPhase).toString();
        const remainingCapacityDisplay = parseFloat(parseFromDecimal(remainingCapacity, tokenDecimals));

        // Calculate APY range
        const apyValues = apys.map((a) => parseInt(a));
        const minApy = Math.min(...apyValues);
        const maxApy = Math.max(...apyValues);

        return {
          phaseIndex: phaseIdx,
          name: `Phase ${phaseIdx + 1}`,
          status,
          apyRange: `${minApy}% - ${maxApy}%`,
          minApy,
          maxApy,
          totalTarget,
          totalStaked: totalStakedPhase,
          remainingCapacity,
          remainingCapacityDisplay,
          canStake: status === StakingTableStatus.ACTIVE && periods.some((p) => p.canStake),
          periods,
        };
      });

      setStakingPhases(phases);
      setProgramError(null);
    } catch (e) {
      console.error('Failed to fetch program data:', e);
      setProgramError('Failed to load staking data');
      dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
    }
  }, [hasCurrency, lgoWeb3, tokenDecimals, dispatch, t]);

  return {
    currentPhaseIndex,
    stakingPhases,
    minDeposit,
    fetchProgramData,
    fetchMinDeposit,
    programError,
  };
};
